<template>
  <component
    :is="tag"
    class="m-button"
    :class="{
      [`${$attrs.class}`]: $attrs.class,
      // Base style
      [`m-button--${buttonStyle}`]: buttonStyle,
      // Skin button is on
      [`m-button--${skin}`]: skin,
      // Size
      [`m-button--${size}`]: !!size,
      // Additional Styles
      'm-button--loading': loading,
      'm-button--block': block,
      'm-button--block-mobile': block === 'mobile',
    }"
    :disabled="!isAriaDisabled && (isDisabled || loading) ? true : null"
    :aria-disabled="isAriaDisabled || loading ? true : null"
    v-bind="attrs"
  >
    <MIcon
      v-if="computedIconLeft"
      :icon="computedIconLeft"
      size="lg"
      class="m-button__icon m-button__icon--left"
    /><slot /><MIcon
      v-if="iconRight"
      :icon="iconRight"
      size="lg"
      class="m-button__icon m-button__icon--right"
    />
    <!-- slot has to be in line with icons to prevent a compiler from adding a white space -->
  </component>
</template>

<script>
import { cloneDeep } from 'lodash-es'

import { clickableTag } from '../../../lib/type-validators'
import MIcon from '../../typography/Icon/MIcon'

/**
 * Use buttons to trigger specific actions.
 * There should always be at least one primary call to action on a page.
 */
export default {
  name: 'MButton',
  components: { MIcon },
  inheritAttrs: false,
  props: {
    /**
     * HTML tag: `a`, `button`, `router-link`, `nuxt-link`
     */
    tag: {
      type: [Object, String],
      default: 'button',
      validator: clickableTag,
    },
    /** The design of the button to indicate what type of action it is */
    buttonStyle: {
      type: String,
      default: 'primary',
      validator: (prop) =>
        ['primary', 'secondary', 'tertiary', 'promo'].includes(prop),
    },
    /** Button size */
    size: {
      type: String,
      default: null,
      validator: (prop) => ['default', 'small', 'large'].includes(prop),
    },
    /**
     * Full width, optionally mobile only
     */
    block: {
      type: [Boolean, String],
      default: null,
      validator: (prop) => [true, false, 'mobile'].includes(prop),
    },
    /**
     * Icon on the left
     */
    iconLeft: {
      type: String,
      default: null,
    },
    /**
     * Icon on the right
     */
    iconRight: {
      type: String,
      default: null,
    },
    /**
     * Loading state
     */
    loading: {
      type: Boolean,
      default: false,
    },
    /**
     * The skin the button is displayed on
     */
    skin: {
      type: String,
      default: null,
      validator: (prop) =>
        ['light', 'dark', 'medium', 'promo', 'grey', 'mid'].includes(prop),
    },
    /**
     * Discouraged
     */
    isDisabled: {
      type: Boolean,
      default: false,
    },
    /**
     * Applies disabled styles, but isn't invisible for screen readers.
     *
     * For loading states – both `isAriaDisabled` and `loading` should be enabled to
     * prevent the `disabled` attribute from applying.
     *
     * 🚨 For use outside of MForm – the keyboard submit event still needs to be handled.
     */
    isAriaDisabled: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    attrs() {
      const clonedAttrs = cloneDeep(this.$attrs)

      // Button should not allow interactions, if it's in either disabled or loading state
      if (this.isDisabled || this.isAriaDisabled || this.loading) {
        delete clonedAttrs.onClick
        delete clonedAttrs.onKeyup
        delete clonedAttrs.onKeydown
      }

      return clonedAttrs
    },
    computedIconLeft() {
      if (this.loading) {
        return 'loader'
      }

      return this.iconLeft
    },
  },
}
</script>

<style lang="scss" scoped>
@use '../../../sass/framework/mixins/breakpoint';
@use '../../../sass/framework/mixins/buttons';

.m-button {
  @include buttons.button;

  &__icon {
    display: block;
    line-height: 1;
    pointer-events: none; // to stop vue-click-outside directive from thinking element is missing, will rely on parent click event instead
  }

  &--primary {
    @include buttons.button--primary;
  }

  &--secondary {
    @include buttons.button--secondary;

    &.m-button--dark {
      @include buttons.button--secondary-dark;
    }
  }

  &--tertiary {
    @include buttons.button--tertiary;
  }

  &--promo {
    @include buttons.button--promo;
  }

  &--small {
    // Compensate for 1px border
    padding: calc(var(--spacing-8) - 1px) var(--spacing-16);
  }

  &--large {
    // Compensate for 1px border
    padding: calc(var(--spacing-16) - 1px) var(--spacing-24);
  }

  &--block {
    display: flex;
    width: 100%;

    &-mobile {
      @include breakpoint.breakpoint(large-mobile-up) {
        display: inline-flex;
        width: auto;
      }
    }
  }

  &--loading {
    @keyframes loading-spin {
      0% {
        transform: rotate(0deg);
      }
      100% {
        transform: rotate(360deg);
      }
    }
    .m-button__icon--left {
      animation: loading-spin 2.5s infinite linear;
    }
  }
}
</style>
